/**
 * @fileoverview Email signature editor (MOR-842).
 *
 * Two modes over a single HTML value:
 *  - "Editor" — TipTap rich-text (bold / italic / lists / links / tables /
 *    images / colours). Paste a signature from Outlook, Gmail, Word, or a
 *    browser preview and it lands as rich content — no HTML tab required.
 *  - "HTML" — raw textarea for power users who want to inspect or tweak the
 *    markup, with a sandboxed live preview.
 *
 * TipTap still normalises markup outside its schema; the HTML tab remains
 * for signatures that must keep unsupported quirks intact. The parent saves
 * the HTML alongside a derived plain-text part (`htmlToPlainText`) — the
 * send pipeline appends one per MIME part.
 */

import { Extension } from "@tiptap/core";
import { useEditor, useEditorState, EditorContent } from "@tiptap/react";
import type { Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { TableKit } from "@tiptap/extension-table";
import { TextStyleKit } from "@tiptap/extension-text-style";
import { PreserveStyles } from "@/ui/composed/editor/preserve-styles-extension";
import {
  Bold,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Underline as UnderlineIcon,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import { Textarea } from "@/ui/primitives/textarea";
import { cn } from "@/utils/misc";
import { normalizeSignatureHtml } from "./signatureHtml";

/**
 * Markup TipTap drops outright. Tables, images, and inline styles survive
 * (see `PreserveStyles` / `EmailTableAttributes`), but `<style>` blocks and
 * Outlook conditional comments have nowhere to live in the schema.
 */
function looksLikeComplexHtml(html: string): boolean {
  return /<style\b/i.test(html) || /<!--\[if/i.test(html);
}

/**
 * True when plain-text clipboard content is clearly HTML source (e.g. the
 * user copied markup from a view-source / email-client HTML pane) rather
 * than a rich paste that already carries a `text/html` flavour.
 */
function looksLikeHtmlSource(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed.startsWith("<")) return false;
  return /<\/?(p|div|table|tr|td|span|br|img|a|b|strong|i|em|font)\b/i.test(
    trimmed,
  );
}

function serialise(html: string): string {
  if (html === "<p></p>") return "";
  // Store delivery-ready HTML: mail clients don't get our editor stylesheet.
  return normalizeSignatureHtml(html);
}

export function SignatureEditor({
  value,
  onChange,
  idPrefix,
}: {
  /** The signature HTML ("" = none). */
  value: string;
  onChange: (html: string) => void;
  /** Unique prefix for element ids when several editors share a page. */
  idPrefix: string;
}) {
  // Always open on the visual editor — paste from mail clients works there.
  // The HTML tab stays available for source-level tweaks.
  const [mode, setMode] = useState<string>("editor");

  return (
    <Tabs value={mode} onValueChange={setMode}>
      <TabsList className="h-8">
        <TabsTrigger value="editor" className="text-xs">
          Editor
        </TabsTrigger>
        <TabsTrigger value="html" className="text-xs">
          HTML
        </TabsTrigger>
      </TabsList>
      <TabsContent value="editor" className="mt-2">
        <SignatureRichEditor value={value} onChange={onChange} />
        <p className="mt-1.5 text-xs text-muted-foreground">
          Paste a signature from Outlook, Gmail, Word, or any browser preview —
          formatting, links, tables, and images are kept.
        </p>
        {looksLikeComplexHtml(value) && (
          <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
            This signature uses stylesheet blocks the editor can't hold. Use the
            HTML tab to keep the original source intact.
          </p>
        )}
      </TabsContent>
      <TabsContent value="html" className="mt-2 space-y-2">
        <Textarea
          id={`${idPrefix}-signature-html`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={6}
          spellCheck={false}
          className="font-mono text-xs"
          placeholder="Optional: paste or edit the HTML source here."
        />
        {value.trim().length > 0 && (
          <div className="rounded-md border">
            <p className="border-b bg-muted/30 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Preview
            </p>
            {/* Sandboxed (no scripts) so pasted markup can't run in the app. */}
            <iframe
              title="Signature preview"
              sandbox=""
              srcDoc={`<!doctype html><html><head><meta charset="utf-8"><style>body{margin:12px;font-family:system-ui,sans-serif;font-size:14px}table{border-collapse:collapse}img{max-width:100%;height:auto}</style></head><body>${value}</body></html>`}
              className="h-32 w-full bg-white"
            />
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
}

/**
 * Legacy presentational attributes email clients still emit instead of CSS
 * (Outlook in particular). TipTap drops unknown attributes, which collapses
 * a signature's column widths and cell alignment on paste.
 */
const EmailTableAttributes = Extension.create({
  name: "emailTableAttributes",

  addGlobalAttributes() {
    const passthrough = (name: string) => ({
      default: null,
      parseHTML: (el: HTMLElement) => el.getAttribute(name),
      renderHTML: (attrs: Record<string, unknown>) =>
        attrs[name] ? { [name]: attrs[name] } : {},
    });

    return [
      {
        types: ["table", "tableRow", "tableCell", "tableHeader"],
        attributes: {
          width: passthrough("width"),
          height: passthrough("height"),
          align: passthrough("align"),
          valign: passthrough("valign"),
          bgcolor: passthrough("bgcolor"),
        },
      },
      {
        types: ["image"],
        attributes: {
          width: passthrough("width"),
          height: passthrough("height"),
        },
      },
    ];
  },
});

/**
 * TipTap surface tuned for email signatures. Richer than NotesEditor
 * (tables + images + text styles) so a clipboard paste from a mail client
 * survives; no AI / voice chrome.
 */
function SignatureRichEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (html: string) => void;
}) {
  const lastEmittedRef = useRef<string>(value);
  const editorRef = useRef<Editor | null>(null);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        // Standalone Underline / Link below — disable StarterKit copies.
        underline: false,
        link: false,
      }),
      Underline,
      // No class override here — a pasted signature styles its own links,
      // and forcing the app's link colour would misrepresent the send.
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      // Colours / font-size / font-family from Outlook/Gmail pastes.
      TextStyleKit,
      TableKit.configure({
        table: { resizable: false },
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      // Full inline-style passthrough: no sibling extension here claims
      // paragraph line-height / margins / alignment, so nothing is stripped.
      PreserveStyles.configure({ paragraphStripProps: [] }),
      EmailTableAttributes,
    ],
    [],
  );

  const editor = useEditor({
    extensions,
    content: value || "",
    onUpdate: ({ editor: e }) => {
      const html = serialise(e.getHTML());
      if (html === lastEmittedRef.current) return;
      lastEmittedRef.current = html;
      onChange(html);
    },
    editorProps: {
      attributes: {
        // Deliberately NOT `prose`: typography utilities override the
        // signature's own fonts, colours, and spacing. The canvas is white
        // in both themes because signature colours assume an email
        // background, not the app's dark surface.
        class: cn(
          "signature-editor-content bg-white text-sm text-[#111827]",
          "min-h-[100px] px-3 py-2 focus:outline-none",
        ),
        "data-placeholder": "Kind regards…",
      },
      /**
       * Prefer rich clipboard HTML (default TipTap path). If the user pasted
       * raw HTML source as plain text, parse it as HTML instead of inserting
       * the tags as literal characters.
       */
      handlePaste: (_view, event) => {
        const clipboard = event.clipboardData;
        if (!clipboard) return false;

        const htmlFlavour = clipboard.getData("text/html")?.trim() ?? "";
        if (htmlFlavour.length > 0) {
          // Let TipTap's default HTML paste run — schema now accepts tables
          // and images, so Outlook/Gmail pastes land as rich content.
          return false;
        }

        const plain = clipboard.getData("text/plain") ?? "";
        if (!looksLikeHtmlSource(plain)) return false;

        event.preventDefault();
        // `insertContent` parses an HTML string against the schema.
        editorRef.current?.commands.insertContent(plain.trim());
        return true;
      },
    },
  });

  editorRef.current = editor;

  // Keep the document in sync when the parent swaps value (e.g. HTML tab
  // edit, or load of a saved signature). Skip when it matches what we just
  // emitted so live typing isn't clobbered.
  useEffect(() => {
    if (!editor) return;
    const incoming = value || "";
    if (incoming !== lastEmittedRef.current) {
      lastEmittedRef.current = incoming;
      editor.commands.setContent(incoming, { emitUpdate: false });
    }
  }, [editor, value]);

  if (!editor) return null;

  return (
    <div className="overflow-hidden rounded-md border border-input bg-background">
      <SignatureToolbar editor={editor} />
      <EditorContent editor={editor} />
      <style>{SIGNATURE_EDITOR_CSS}</style>
    </div>
  );
}

/**
 * Scoped rules for the signature canvas. Deliberately minimal: the editor is
 * a WYSIWYG of what recipients receive, so it must NOT add borders, padding,
 * or paragraph margins the signature itself doesn't declare inline.
 */
const SIGNATURE_EDITOR_CSS = `
.signature-editor-content table {
  border-collapse: collapse;
  width: auto;
  max-width: 100%;
}
.signature-editor-content p {
  margin: 0;
}
.signature-editor-content img {
  max-width: 100%;
}
`;

function SignatureToolbar({ editor }: { editor: Editor }) {
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      isBold: e?.isActive("bold") ?? false,
      isItalic: e?.isActive("italic") ?? false,
      isUnderline: e?.isActive("underline") ?? false,
      isBulletList: e?.isActive("bulletList") ?? false,
      isOrderedList: e?.isActive("orderedList") ?? false,
      isLink: e?.isActive("link") ?? false,
    }),
  });

  return (
    <div className="flex items-center gap-0.5 border-b border-border px-1.5 py-1">
      <ToolbarButton
        active={state?.isBold ?? false}
        onClick={() => editor.chain().focus().toggleBold().run()}
        ariaLabel="Bold"
      >
        <Bold className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        active={state?.isItalic ?? false}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        ariaLabel="Italic"
      >
        <Italic className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        active={state?.isUnderline ?? false}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        ariaLabel="Underline"
      >
        <UnderlineIcon className="h-3.5 w-3.5" />
      </ToolbarButton>
      <div className="mx-1 h-4 w-px bg-border" />
      <ToolbarButton
        active={state?.isBulletList ?? false}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        ariaLabel="Bullet list"
      >
        <List className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        active={state?.isOrderedList ?? false}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        ariaLabel="Numbered list"
      >
        <ListOrdered className="h-3.5 w-3.5" />
      </ToolbarButton>
      <div className="mx-1 h-4 w-px bg-border" />
      <LinkPopover editor={editor} isLink={state?.isLink ?? false} />
    </div>
  );
}

function ToolbarButton({
  active,
  onClick,
  ariaLabel,
  children,
}: {
  active: boolean;
  onClick: () => void;
  ariaLabel: string;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={ariaLabel}
      aria-pressed={active}
      className={cn("h-7 w-7", active && "bg-muted text-foreground")}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

function LinkPopover({ editor, isLink }: { editor: Editor; isLink: boolean }) {
  const [open, setOpen] = useState(false);
  const [href, setHref] = useState("");

  const onOpenChange = useCallback(
    (next: boolean) => {
      if (next) {
        const current = editor.getAttributes("link").href as string | undefined;
        setHref(current ?? "");
      }
      setOpen(next);
    },
    [editor],
  );

  const apply = () => {
    const trimmed = href.trim();
    if (!trimmed) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: trimmed })
        .run();
    }
    setOpen(false);
  };

  const remove = () => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange} modal={false}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Link"
          aria-pressed={isLink}
          className={cn("h-7 w-7", isLink && "bg-muted text-foreground")}
        >
          <LinkIcon className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-3"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-2">
          <Input
            placeholder="https://…"
            value={href}
            onChange={(e) => setHref(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                apply();
              }
            }}
            autoFocus
          />
          <div className="flex items-center justify-between gap-2">
            {isLink ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={remove}
                className="gap-1"
              >
                <X className="h-3 w-3" />
                Remove
              </Button>
            ) : (
              <span />
            )}
            <Button size="sm" onClick={apply}>
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
